import React from 'react';
import { motion, AnimatePresence } from "motion/react";
import { GENDER_OPTIONS } from '@/features/user/constants';

interface GenderSelectorProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
}

const GenderSelector: React.FC<GenderSelectorProps> = ({ value, onChange, label = "Gender" }) => {
  return (
    <motion.div 
      className="w-full"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <motion.label 
        className="block text-sm font-medium text-gray-400 mb-3"
        initial={{ opacity: 0, x: -10 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.1, duration: 0.3 }}
      >
        {label}
      </motion.label>
      <motion.div 
        className="flex gap-4"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2, duration: 0.3 }}
      >
        {GENDER_OPTIONS.map((option, index) => (
          <motion.label
            key={option.value}
            className="flex items-center gap-3 cursor-pointer group"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 + index * 0.1, duration: 0.3 }}
            whileHover={{ scale: 1.05 }}
          >
            <div className="relative flex items-center justify-center">
              <input
                type="radio"
                name="gender"
                value={option.value}
                checked={value === option.value}
                onChange={() => onChange(option.value)}
                className="sr-only"
              />
              <motion.div 
                className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                  value === option.value
                    ? 'border-violet-500 bg-violet-500'
                    : 'border-gray-600 group-hover:border-violet-400'
                }`}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                transition={{ type: "spring", stiffness: 400, damping: 10 }}
              >
                <AnimatePresence>
                  {value === option.value && (
                    <motion.div 
                      className="w-2 h-2 bg-white rounded-full"
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      exit={{ scale: 0 }}
                      transition={{ type: "spring", stiffness: 400, damping: 10 }}
                    />
                  )}
                </AnimatePresence>
              </motion.div>
            </div>
            <motion.span 
              className={`text-sm ${
                value === option.value
                  ? 'text-violet-400 font-medium'
                  : 'text-gray-300 group-hover:text-gray-100'
              }`}
              whileHover={{ x: 2 }}
              transition={{ type: "spring", stiffness: 400, damping: 10 }}
            >
              {option.label}
            </motion.span>
          </motion.label>
        ))}
      </motion.div>
    </motion.div>
  );
};

export default GenderSelector;
